/**
 * <EnginesMarquee /> — TWO infinite-loop rows of CARTIE engine cards,
 * scrolling in OPPOSITE directions for visual rhythm. Each card is a
 * clickable Link to that engine's deep-dive page.
 *
 * v34.24 (Feb 2026): rebuilt per user video reference — 2 rows where
 * top row scrolls left→right and bottom row scrolls right→left. Cards
 * are real <Link> components routing to verified pages in App.js.
 *
 * Design notes:
 *   - 280px cards · slate-900 surface with subtle gradient
 *   - Per-engine icon + brand-tinted tile + title + one-line tagline
 *   - Hover: -translate-y-1, border brightens, icon-tile brightens,
 *     tinted shadow glow, "click to drill in" pointer
 *   - Pauses BOTH rows on hover so visitors can read individual cards
 *   - Same seamless duplicated-track CSS keyframe technique
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Cpu, Database, Layers, Network, Shield, Zap,
  Activity, AlertTriangle, BarChart3, Bot, Code,
  DollarSign, Eye, FileSearch, Flame, GitBranch, Leaf,
  Radar, Receipt, Scissors, MessagesSquare, Scale, Gauge, Search, Sparkles, Target, TrendingUp, Users,
} from 'lucide-react';
import { ENGINE_COUNT } from '../constants/product';

// v35.28 (Feb 2026) — Honest dedupe. Engines reduced from 27 marquee cards
// (with 9 duplicates / non-engine product surfaces) down to the **18 real
// engines** we actually built. Adjacent-color collision rule preserved:
// each card's accent ≠ left neighbour ≠ right neighbour.
//
// DELETED dups (with their canonical homes):
//   Retry Storm Detector   →  merged into Anomaly Detection v2 (#14)
//   Cache Miss Hunter      →  merged into Semantic Cache (#2)
//   Smart Router           →  merged into Real-Time Token Arbitrage (#27)
//   Cross-Cloud Arbitrage  →  merged into Real-Time Token Arbitrage (#27)
//   Causal FinOps Bot      →  merged into Bill Detective (#29)
//   Spike Autopsy          →  merged into Bill Detective (#29)
//   Invoice Autopsy        →  merged into Bill Detective (#29)
//   Token Leak Detector    →  merged into Anomaly Detection v2 (#14)
//   Wall of Tenants        →  merged into Wall of Fame
//   (Plus 4 product surfaces moved OUT of "engines" — Pilot Activation
//    Gate, Strategic Overdrive, Burning Cash Ticker — they are dashboards
//    & tools, not algorithmic engines.)
const ROW_A = [
  { name: 'Reasoning Efficiency',         tag: 'Cut loop-waste 18% per intent',     icon: Cpu,             accent: 'emerald', to: '/reasoning-efficiency' },
  { name: 'Semantic Cache + Miss Hunter', tag: 'OpenAI-embedding cache + leak hunt',icon: Database,        accent: 'amber',   to: '/semantic-cache' },
  { name: 'Prompt Compactor',             tag: 'LLMLingua-grade compression',       icon: Code,            accent: 'violet',  to: '/prompt-compactor' },
  { name: 'Shadow AI Audit',              tag: 'Find every rogue ChatGPT seat',     icon: Search,          accent: 'sky',     to: '/shadow-ai-seats' },
  { name: 'Cost Bisect',                  tag: 'Binary-search a spend regression',  icon: Target,          accent: 'orange',  to: '/cost-bisect' },
  { name: 'CARTIE Lens',                  tag: 'Per-pixel cost attribution',        icon: Eye,             accent: 'cyan',    to: '/cartie-lens' },
  { name: 'K8s Shapley Attribution',      tag: 'Fair per-pod chargebacks',          icon: Layers,          accent: 'violet',  to: '/kubernetes' },
  { name: 'Carbon-Cost Pareto',           tag: 'Scope-3 + dollar joint front',      icon: Leaf,            accent: 'emerald', to: '/carbon-cost' },
  { name: 'Auto-Tagger',                  tag: 'Zero-config resource tagging',      icon: FileSearch,      accent: 'indigo',  to: '/tag-canon' },
  { name: 'Output Token Diet',            tag: 'Response bloat — output costs 4× input', icon: Scissors,   accent: 'rose',    to: '/output-diet',       badge: 'NEW' },
  { name: 'Decision Ledger',              tag: 'Two futures per decision · graded forecasts · human-in-loop', icon: Scale, accent: 'amber', to: '/decision-ledger', badge: 'NEW' },
  { name: 'Cost SLO Guard',               tag: 'SRE burn-rate math for budgets — know the breach date early', icon: Gauge, accent: 'emerald', to: '/cost-slo', badge: 'NEW' },
];

const ROW_B = [
  { name: 'Bill Detective',               tag: 'Engineer + commit + PR behind every spike', icon: Search,  accent: 'emerald', to: '/bill-detective',    badge: 'NEW' },
  { name: 'Idle Resource Killer',         tag: '14-day shadow ramp · email-OTP gate',icon: AlertTriangle,  accent: 'rose',    to: '/idle-killer',       badge: 'NEW' },
  { name: 'Real-Time Token Arbitrage',    tag: '12-provider proxy · cache · cascade', icon: Zap,           accent: 'amber',   to: '/engine-27' },
  { name: 'RAG Embedding Cache',          tag: '100% on cache hit',  icon: Database,        accent: 'teal',    to: '/engine-27' },
  { name: 'Anomaly Detection v2',         tag: 'Auto-resolve · token-leak alarm',   icon: Activity,        accent: 'orange',  to: '/anomaly-detection' },
  { name: 'AI Agent Economics',           tag: 'Runaway detect · hard-cap · per-run ROI', icon: Bot,        accent: 'fuchsia', to: '/agent-economics',   badge: 'WORLD-FIRST' },
  { name: 'Embedding Store Audit',        tag: 'Vector graveyard · cold-namespace finder', icon: Radar,    accent: 'pink',    to: '/embedding-audit',   badge: 'WORLD-FIRST' },
  { name: 'Pipeline DAG Dedup',           tag: 'Airflow + Dagster duplicate finder', icon: GitBranch,      accent: 'indigo',  to: '/pipeline-dedup',    badge: 'WORLD-FIRST' },
  { name: 'Egress Predictor',             tag: 'Snowflake → Bedrock routing',       icon: TrendingUp,      accent: 'violet',  to: '/egress-predictor' },
  { name: 'PR Cost Predictor',            tag: 'Block deploys before they hurt',    icon: Shield,          accent: 'sky',     to: '/pr-cost-predictor' },
  { name: 'Decision Simulator',           tag: 'What-if before the commit',         icon: Sparkles,        accent: 'orange',  to: '/decision-simulator' },
  { name: 'Budget Negotiator™',           tag: 'Finance↔Eng auto-negotiation · patent-pending', icon: DollarSign, accent: 'cyan', to: '/budget-negotiator' },
  { name: 'Memory Compactor',             tag: 'Kill the O(N²) chat-history resend tax', icon: MessagesSquare, accent: 'sky', to: '/memory-compactor', badge: 'NEW' },
  { name: 'PTU ROI Planner',              tag: 'Azure PTUs vs pay-as-you-go — the commit math nobody does', icon: Cpu, accent: 'violet', to: '/ptu-planner', badge: 'NEW' },
];

// Total cards: 12 (ROW_A) + 14 (ROW_B) = 26 honest engines.

// Per-engine accent tokens — v34.25 (Feb 2026): switched from subtle
// translucent tints → bold neon-lit tiles per user reference. Each tile
// is a SOLID saturated brand color with a strong outer glow ring + white
// icon on top, like the "lamp" effect in the reference image. Palette
// widened to 10 hues so the 26 cards feel like a vibrant arcade strip,
// not a monotone wash.
const ACCENT = {
  emerald: { iconBg: 'bg-emerald-500',  iconShadow: 'shadow-[0_0_24px_0_rgba(16,185,129,0.6)]',  hoverBorder: 'hover:border-emerald-400/50' },
  sky:     { iconBg: 'bg-sky-500',      iconShadow: 'shadow-[0_0_24px_0_rgba(14,165,233,0.6)]',  hoverBorder: 'hover:border-sky-400/50' },
  cyan:    { iconBg: 'bg-cyan-500',     iconShadow: 'shadow-[0_0_24px_0_rgba(6,182,212,0.6)]',   hoverBorder: 'hover:border-cyan-400/50' },
  amber:   { iconBg: 'bg-amber-500',    iconShadow: 'shadow-[0_0_24px_0_rgba(245,158,11,0.65)]', hoverBorder: 'hover:border-amber-400/50' },
  orange:  { iconBg: 'bg-orange-500',   iconShadow: 'shadow-[0_0_24px_0_rgba(249,115,22,0.65)]', hoverBorder: 'hover:border-orange-400/50' },
  rose:    { iconBg: 'bg-rose-500',     iconShadow: 'shadow-[0_0_24px_0_rgba(244,63,94,0.65)]',  hoverBorder: 'hover:border-rose-400/50' },
  pink:    { iconBg: 'bg-pink-500',     iconShadow: 'shadow-[0_0_24px_0_rgba(236,72,153,0.65)]', hoverBorder: 'hover:border-pink-400/50' },
  violet:  { iconBg: 'bg-violet-500',   iconShadow: 'shadow-[0_0_24px_0_rgba(139,92,246,0.65)]', hoverBorder: 'hover:border-violet-400/50' },
  teal:    { iconBg: 'bg-teal-500',     iconShadow: 'shadow-[0_0_24px_0_rgba(20,184,166,0.6)]',  hoverBorder: 'hover:border-teal-400/50' },
  indigo:  { iconBg: 'bg-indigo-500',   iconShadow: 'shadow-[0_0_24px_0_rgba(99,102,241,0.6)]',  hoverBorder: 'hover:border-indigo-400/50' },
  fuchsia: { iconBg: 'bg-fuchsia-500',  iconShadow: 'shadow-[0_0_24px_0_rgba(217,70,239,0.65)]', hoverBorder: 'hover:border-fuchsia-400/50' },
};

let injected = false;
const ensureKeyframe = () => {
  if (typeof document === 'undefined' || injected) return;
  const style = document.createElement('style');
  style.setAttribute('data-cartie', 'engines-marquee-kf');
  style.textContent = `
    @keyframes marquee-x-left  { from { transform: translate3d(0, 0, 0); } to { transform: translate3d(-50%, 0, 0); } }
    @keyframes marquee-x-right { from { transform: translate3d(-50%, 0, 0); } to { transform: translate3d(0, 0, 0); } }
    .engines-marquee-track:hover .engines-marquee-flow {
      animation-play-state: paused;
    }
  `;
  document.head.appendChild(style);
  injected = true;
};

// Single card renderer — reused by both rows.
// v34.26 (Feb 2026): horizontal layout — icon + name on the SAME row,
// tagline below. Matches user reference. Cards narrowed to 240px so
// more fit in view; vertical space saved by ditching the stacked
// icon-then-title arrangement.
const EngineCard = ({ engine }) => {
  const a = ACCENT[engine.accent] || ACCENT.emerald;
  const Icon = engine.icon;
  return (
    <Link
      to={engine.to}
      data-testid={`engine-card-${engine.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className={`shrink-0 w-[260px] rounded-xl border border-white/10 bg-slate-900 backdrop-blur px-4 py-3.5 group transition-all hover:-translate-y-0.5 hover:shadow-2xl cursor-pointer ${a.hoverBorder}`}
    >
      {/* Top row — icon left, name + arrow right */}
      <div className="flex items-center gap-3 mb-1">
        <div className={`relative shrink-0 w-9 h-9 rounded-lg ${a.iconBg} ${a.iconShadow} flex items-center justify-center ring-1 ring-white/20 group-hover:scale-105 transition-transform`}>
          <Icon className="w-4 h-4 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0 flex items-center justify-between gap-1.5">
          <h3 className="text-[14px] font-bold text-white leading-tight tracking-tight truncate">{engine.name}</h3>
          <span className="flex items-center gap-1.5 shrink-0">
            {engine.badge && (
              <span className="text-[8px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/40" data-testid={`engine-badge-${engine.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{engine.badge}</span>
            )}
            <ArrowRight className="shrink-0 w-3.5 h-3.5 text-slate-400 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
          </span>
        </div>
      </div>

      {/* Tagline — full width below the icon row, slight left indent to align under name */}
      <p className="text-[11.5px] text-slate-400 leading-snug pl-12">{engine.tag}</p>
    </Link>
  );
};

const EnginesMarquee = () => {
  useEffect(() => { ensureKeyframe(); }, []);

  return (
    <section
      className="relative py-16 border-y border-emerald-500/10 bg-slate-950 overflow-hidden"
      data-testid="landing-engines-marquee"
    >
      <div className="text-center mb-10 px-4">
        <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-300/80">
          <span className="w-8 h-px bg-emerald-400/40" />
          {ENGINE_COUNT} proprietary engines · cartie-only
          <span className="w-8 h-px bg-emerald-400/40" />
        </div>
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white max-w-2xl mx-auto">
          The math that runs the savings.
        </h2>
        <p className="mt-2 text-sm text-slate-400">Click any engine to drill in.</p>
      </div>

      {/* Edge fades */}
      <div className="pointer-events-none absolute left-0 top-[140px] bottom-[100px] w-32 z-10 bg-slate-950 bg-gradient-to-r from-slate-950 to-transparent" />
      <div className="pointer-events-none absolute right-0 top-[140px] bottom-[100px] w-32 z-10 bg-slate-950 bg-gradient-to-l from-slate-950 to-transparent" />

      {/* Row A — top, scrolls LEFT (right-to-left motion) */}
      <div className="relative engines-marquee-track mb-4">
        <div
          className="engines-marquee-flow flex items-stretch gap-4 w-max"
          style={{ animation: 'marquee-x-left 80s linear infinite' }}
          data-testid="engines-marquee-row-a"
        >
          {[...ROW_A, ...ROW_A].map((e, i) => <EngineCard key={`a-${i}`} engine={e} />)}
        </div>
      </div>

      {/* Row B — bottom, scrolls RIGHT (left-to-right motion) */}
      <div className="relative engines-marquee-track">
        <div
          className="engines-marquee-flow flex items-stretch gap-4 w-max"
          style={{ animation: 'marquee-x-right 80s linear infinite' }}
          data-testid="engines-marquee-row-b"
        >
          {[...ROW_B, ...ROW_B].map((e, i) => <EngineCard key={`b-${i}`} engine={e} />)}
        </div>
      </div>

      {/* v34.29 (Feb 2026): Scorecard summary card merged INTO the engines
          section per user feedback — they were the same story told twice
          (26 engines / 27 capabilities) in two separate places on the
          landing. Now visitors get the engine ribbon, then ONE summary
          card with the head-to-head receipt + a single CTA to the full
          scorecard. No duplication. */}
      <div className="max-w-5xl mx-auto px-4 mt-12">
        <Link
          to="/honest-scorecard"
          className="group relative block rounded-xl border border-white/10 bg-slate-900 backdrop-blur overflow-hidden hover:border-emerald-500/40 hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-emerald-500/10 transition-all duration-300"
          data-testid="engines-scorecard-card"
        >
          {/* Terminal chrome */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 bg-slate-950">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
              <span className="ml-3 text-[11px] text-slate-500 font-mono">build-output · finops-feature-matrix.log</span>
            </div>
            <span className="text-[10px] text-emerald-400/80 font-mono">Σ ./run-comparison</span>
          </div>

          {/* Hero scoreline */}
          <div className="px-6 sm:px-8 py-7 sm:py-8 grid sm:grid-cols-[1.2fr_auto] gap-6 items-center">
            <div className="font-mono">
              <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1.5">RESULT — 26 ENGINES · 32 CAPABILITIES VS 9 INCUMBENTS</div>
              <div className="flex items-baseline gap-3 mb-1.5 flex-wrap">
                <span className="font-display text-4xl sm:text-5xl font-black tabular-nums text-emerald-300">31/32</span>
                <span className="text-slate-500 text-sm">capabilities CARTIE passes</span>
              </div>
              <div className="text-[11px] text-slate-500 leading-relaxed">
                <span className="text-emerald-400">✓</span> 265 fails across 9 incumbents
                <span className="mx-2 text-slate-700">·</span>
                <span className="text-amber-300">1 honest gap</span>
                <span className="text-slate-600"> (SOC 2 Type II · Q3 2026)</span>
              </div>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 font-semibold text-sm group-hover:bg-emerald-500 group-hover:text-slate-950 group-hover:border-emerald-400 group-hover:gap-3 transition-all w-fit">
              Open the full 32 × 9 scorecard
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </Link>
        <p className="mt-4 text-[11px] text-slate-500 font-mono tracking-wide text-center">
          // Methodology: features verified against public docs · changelogs · pricing pages on Feb 28 2026.
          Submit corrections at <a href="mailto:hello@cartieai.com" className="text-emerald-400 underline-offset-4 hover:underline">hello@cartieai.com</a> — we update within 48h.
        </p>
      </div>
    </section>
  );
};

export default EnginesMarquee;
